import { Component, ElementRef, NgZone, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ApiRoutes } from '@sb/library/api';
import { BaseViewComponent, DialogManager, Environment, ErrorMessages, Errors, FormBase, FormContainerKind, NameCodeWithID, Optional, PaymentContext, PaymentContextScope, PaymentProcessingScope, PaymentSourceMode, Required, boolean, string } from '@sb/library/base';
import { ErrorCode, NameCodePair, PayPalCommercePlatformCreateOrderInputItem, PayPalCommercePlatformCreateTokenInputItem, PayPalCommercePlatformCreateVaultSetupTokenInputItem, PayPalCommercePlatformOrderResultItem, PayPalCommercePlatformPaymentSourceInputItem, PaymentProcessorType, PaymentSourceIntent, PaymentSourceType, SurchargeResultItem, SurchargeType } from '@sb/library/data-models';
import { CreateOrderActions, CreateOrderData, PayPalNamespace, loadScript } from '@paypal/paypal-js';
import { Observable, firstValueFrom } from 'rxjs';
import { PaymentErrorDialogComponent, PaymentErrorDialogScope } from '../../dialogs/payment-error-dialog.component';

class PayPalAddPaymentSourceForm extends FormBase {
    AutoPay = Optional(boolean);
    SavePaymentSource = Optional(boolean);
    PostalCode = Required(string);
    Country = Required(NameCodeWithID);
    State = Required(NameCodeWithID);

    // These controls are used internally for the PayPal form fields (they are only used for error display)
    CardNumber = Optional(string);
    ExpiryDate = Optional(string);
    CVV = Optional(string);
    CardHolderName = Optional(string);
}

@Component({
    selector: 'lib-payment-sources-add-paypal',
    templateUrl: './payment-sources-add-paypal.component.html',
    styleUrls: ['./payment-sources-add-paypal.component.scss']
})
export class PaymentSourcesAddPayPalComponent extends BaseViewComponent implements PaymentErrorDialogScope {

    Scope = this.SetScope({
        Implements: PaymentErrorDialogScope,
        Requires: [PaymentContextScope, PaymentProcessingScope]
    });

    // Element used to match style for PayPal form fields
    @ViewChild('payPalInputStyleElement', { static: true }) payPalInputStyleElement!: ElementRef<HTMLElement>;

    readonly cardNumberErrors = new Errors();
    readonly expiryDateErrors = new Errors();
    readonly cvvErrors = new Errors();
    readonly cardHolderNameErrors = new Errors();

    private readonly _defaultButtonErrorDialogText = $localize`:@@CommonAddPaymentSourceAuthorizationErrorMessage:There was a problem processing your payment. Please try again or contact support for more help.`;
    private readonly _defaultCardErrorDialogText = $localize`:@@CommonAddPaymentCardAuthorizationErrorMessage:There was a problem processing your card. Please try another card or contact support for more help.`;

    readonly form = PayPalAddPaymentSourceForm.Create(false);

    private _totalAmountToPay: number | null = null;
    private _onlineConvenienceFee: number | null = null;

    private _paymentSourceType: PaymentSourceType = PaymentSourceType.CreditCard;
    private _loading: boolean = true;
    private _loadingPayPalButtons: boolean = true;
    private _loadingCardNumberField: boolean = true;
    private _loadingExpiryField: boolean = true;
    private _loadingCVVField: boolean = true;
    private _loadingNameField: boolean = true;

    private _inputStyle!: CSSStyleDeclaration;
    private _cvvTooltip: string = $localize`:@@CommonAddPaymentSourceCardGenericCVVTooltip:Your CVV (Card Verification Value) is displayed on the back of your card as the last 3 digits`;

    private _countries: NameCodeWithID[] = [];
    private _states: NameCodeWithID[] = [];
    private _defaultState: NameCodeWithID = {
        ID: 'default',
        Name: $localize`:@@CommonAddPaymentSourceCountryStatesDefault:Outside US/Canada`,
        Code: null,
        Default: true
    };

    readonly zipCodeError: ErrorMessages = {
        required: _ => $localize`:@@CommonAddPaymentSourceZipCodeMissingError:Please enter your zip/postal code`
    };

    readonly countryError: ErrorMessages = {
        required: _ => $localize`:@@CommonAddPaymentSourceCountryMissingError:Please select your country`
    };

    readonly stateError: ErrorMessages = {
        required: _ => $localize`:@@CommonAddPaymentSourceStateProvinceMissingError:Please select your state or province`
    };

    private _cardFields: any = null;
    private _orderID: string | null = null;

    private _debugNewTab = false;

    constructor(
        private _Environment: Environment,
        private _DialogManager: DialogManager,
        private _NgZone: NgZone
    ) {
        super();

        this.cardNumberErrors.Override({
            required: _ => $localize`:@@CommonAddPaymentSourceCreditCardNumberMissingError:Please enter your card number`,
            api: _ => $localize`:@@CommonAddPaymentSourceCreditCardNumberInvalid:Please enter a valid card number`
        });

        this.expiryDateErrors.Override({
            required: _ => $localize`:@@CommonAddPaymentSourceCreditCardExpiryDateMissingError:Please enter the card's expiry date`,
            api: _ => $localize`:@@CommonAddPaymentSourceCreditCardExpiryDateInvalid:Please enter a valid expiry date`
        });

        this.cvvErrors.Override({
            required: _ => $localize`:@@CommonAddPaymentSourceCreditCardCVVMissingError:Please enter the CVV (Card Verification Value)`,
            api: _ => $localize`:@@CommonAddPaymentSourceCreditCardCVVInvalid:Please enter a valid CVV (Card Verification Value)`
        });

        this.cardHolderNameErrors.Override({
            required: _ => $localize`:@@CommonAddPaymentSourceCreditCardCardholderNameMissingError:Please enter the card holder's name as it appears on the card`,
            api: _ => $localize`:@@CommonAddPaymentSourceCreditCardCardholderNameInvalid:Please enter a valid card holder name`
        });
    }

    override ngOnInit() {
        super.ngOnInit();

        if (this.Scope.paymentProcessor!.Type != PaymentProcessorType.PayPalCommercePlatform) {
            throw Error('Wrong payment processor type for payment-sources-add-paypal-commerce-platform.component.ts')
        }

        if (this.Scope.paymentProcessor!.StoredPaymentMethodsSupported && this.Scope.mode !== PaymentSourceMode.AnonymousPayment) {
            this.form.controls.SavePaymentSource.setValue(true);
            if (this.Scope.schoolInfo.ForceAutoPay) {
                this.form.controls.AutoPay.setValue(true);
                this.form.controls.AutoPay.Disable();
            }
        }

        this.ConnectTrigger(this.Scope.triggerUpdateAmountWithSurcharge, this.UpdateAmountWithSurcharge);
        this.ConnectTrigger(this.Scope.triggerProcessPaymentSource, this.ProcessPaymentSource);
        this.ConnectTrigger(this.Scope.triggerReenablePaymentProcessorForm, () => this.form.Enable);

        this.GetCountries();

        this.Scope.PaymentSourceTypeChanged?.(PaymentSourceType.CreditCard);
    }
    
    ngAfterViewInit(): void {
        this._inputStyle = getComputedStyle(this.payPalInputStyleElement.nativeElement);

        ApiRoutes.PaymentProcessor.PayPal.CommercePlatform.CreateTokens.Call({
            Parameter1: this.Scope.schoolInfo.ID,
            Parameter2: this.Scope.paymentProcessor!.ID!,
            Body: new PayPalCommercePlatformCreateTokenInputItem({
                IncludeButtons: true
            })
        }).subscribe(tokens => {

            loadScript({
                clientId: this._Environment.Payment!.PayPalCommercePlatform!.ClientID!,
                components: (this.Scope.mode === PaymentSourceMode.Add || this.Scope.context === PaymentContext.SignupWidget) ? ['card-fields'] : ['buttons', 'card-fields'],
                intent: 'capture',
                currency: this.Scope.schoolInfo.CurrencyCode.toUpperCase(),
                dataPartnerAttributionId: this._Environment.Payment!.PayPalCommercePlatform!.BNCode,
                dataClientToken: tokens.CardFieldsToken!,
                merchantId: tokens.MerchantID!,
                dataUserIdToken: (this.Scope.mode === PaymentSourceMode.Add || this.Scope.context === PaymentContext.SignupWidget) ? undefined : tokens.ButtonToken!,
                disableFunding: this.Scope.paymentProcessor!.StoredPaymentMethodsSupported ? ['paylater'] : ['card', 'paylater'],
                enableFunding: ['venmo'],
                buyerCountry: this.isDebug ? 'US' : undefined         // Make Venmo testing possible on Test 443
            }).then((paypal: PayPalNamespace | null) => {
                if (!paypal || !(paypal as any).CardFields) {
                    throw Error;
                }

                if (paypal.Buttons) {
                    paypal.Buttons({
                        createOrder: async (data: CreateOrderData, actions: CreateOrderActions) => {
                            this._NgZone.run(() => {
                                this.form.Disable();
                            });

                            let paymentSourceType: PaymentSourceType;
                            switch (data.paymentSource) {
                                case 'paypal':
                                    paymentSourceType = PaymentSourceType.PayPal;
                                    break;
                                case 'venmo':
                                    paymentSourceType = PaymentSourceType.Venmo;
                                    break;
                                default:
                                    throw Error('Unsupported payment source type');
                            }

                            const result = await firstValueFrom(this.CreateOrder(paymentSourceType));

                            this._orderID = result.PayPalOrderID;
                            return this._orderID;
                        },

                        onApprove: async (data: any, actions: any) => {
                            this._NgZone.run(() => this.ProcessNewPaymentSource());
                        },

                        onCancel: (data: any, actions: any) => {
                            this._NgZone.run(() => {
                                this.Scope.PaymentProcessorReady();
                                this.form.Enable();
                            });
                        },

                        onError: (error: any) => {
                            if (!this._DialogManager.dialogReference) {
                                this._NgZone.run(() => {
                                    this.CreateDialog(PaymentErrorDialogComponent, { IsProcessorError: false, ErrorMessage: this._defaultButtonErrorDialogText });
                                });
                            }
                        },

                        onInit: async (data: any, actions: any) => {
                        }
                    } as any).render("#paypal-button-container")
                            .then(() => this.FieldLoaded('buttons'))
                            .catch(error => {
                                throw Error('PayPal buttons could not be loaded');
                            });
                } else {
                    this._loadingPayPalButtons = false;
                }

                if (this.Scope.paymentProcessor!.StoredPaymentMethodsSupported) {
                    if ((paypal as any).CardFields) {
                        const cardStyle = {
                            'input': {
                                'color': '#000',
                                'font-family': this._inputStyle.fontFamily,
                                'font-size': this._inputStyle.fontSize,
                                'font-weight': this._inputStyle.fontWeight,
                                'letter-spacing': this._inputStyle.letterSpacing,
                                'padding': '7.4px'
                            },
                            '.invalid': {
                                'color': '#000',
                            }
                        }

                        this._cardFields = (paypal as any).CardFields({
                            style: cardStyle,

                            createOrder: this.Scope.mode === PaymentSourceMode.Add
                                ? undefined
                                : async (data: any) => {
                                    const result = await firstValueFrom(this.CreateOrder(PaymentSourceType.CreditCard));

                                    this._orderID = result.PayPalOrderID;
                                    return this._orderID;
                                },

                            createVaultSetupToken: this.Scope.mode !== PaymentSourceMode.Add
                                ? undefined
                                : async (data: any) => {
                                    const formValues = this.form.value;
                                    return firstValueFrom(ApiRoutes.PaymentProcessor.PayPal.CommercePlatform.CreateVaultSetupToken.Call({
                                        Parameter1: this.Scope.schoolInfo.ID,
                                        Parameter2: this.Scope.paymentProcessor!.ID!,
                                        Body: new PayPalCommercePlatformCreateVaultSetupTokenInputItem({
                                            PostalCode: formValues.PostalCode,
                                            CountryCode: formValues.Country!.Code,
                                            ProvStateCode: formValues.State!.Code,
                                            ReturnURL: window.location.href
                                        })
                                    }));
                                },

                            onApprove: async (data: any) => {
                                this._NgZone.run(() => {
                                    if (data.liabilityShift === 'NO' || data.liabilityShift === 'UNKNOWN') {
                                        this.CreateDialog(PaymentErrorDialogComponent, { IsProcessorError: false, ErrorMessage: this._defaultCardErrorDialogText });
                                    } else {
                                        this.ProcessNewPaymentSource(data.vaultSetupToken);
                                    }
                                });
                            },

                            onError: (error: any) => {
                                if (!this._DialogManager.dialogReference) {
                                    this._NgZone.run(() => {
                                        this.CreateDialog(PaymentErrorDialogComponent, { IsProcessorError: false, ErrorMessage: this._defaultCardErrorDialogText });
                                    });
                                }
                            }
                        });

                        if (this._cardFields.isEligible()) {

                            const numberField = this._cardFields.NumberField({
                                inputEvents: {
                                    onChange: (data: any) => this._NgZone.run(() => this.UpdateCardFieldValidity(data.fields.cardNumberField, this.form.controls.CardNumber, false)),
                                    onBlur: (data: any) => this._NgZone.run(() => this.UpdateCardFieldValidity(data.fields.cardNumberField, this.form.controls.CardNumber, true)),
                                    onFocus: (_: any) => {},
                                    onInputSubmitRequest: (_: any) => {}
                                }
                            });

                            const expiryField = this._cardFields.ExpiryField({
                                inputEvents: {
                                    onChange: (data: any) => this._NgZone.run(() => this.UpdateCardFieldValidity(data.fields.cardExpiryField, this.form.controls.ExpiryDate, false)),
                                    onBlur: (data: any) => this._NgZone.run(() => this.UpdateCardFieldValidity(data.fields.cardExpiryField, this.form.controls.ExpiryDate, true)),
                                    onFocus: (_: any) => {},
                                    onInputSubmitRequest: (_: any) => {}
                                }
                            });

                            const cvvField = this._cardFields.CVVField({
                                inputEvents: {
                                    onChange: (data: any) => this._NgZone.run(() => this.UpdateCardFieldValidity(data.fields.cardCvvField, this.form.controls.CVV, false)),
                                    onBlur: (data: any) => this._NgZone.run(() => this.UpdateCardFieldValidity(data.fields.cardCvvField, this.form.controls.CVV, true)),
                                    onFocus: (_: any) => {},
                                    onInputSubmitRequest: (_: any) => {}
                                }
                            });

                            const nameField = this._cardFields.NameField({
                                inputEvents: {
                                    onChange: (data: any) => this._NgZone.run(() => this.UpdateCardFieldValidity(data.fields.cardNameField, this.form.controls.CardHolderName, false)),
                                    onBlur: (data: any) => this._NgZone.run(() => this.UpdateCardFieldValidity(data.fields.cardNameField, this.form.controls.CardHolderName, true)),
                                    onFocus: (_: any) => {},
                                    onInputSubmitRequest: (_: any) => {}
                                }
                            });

                            numberField.setAttribute('placeholder', '4111 1111 1111 1111');
                            nameField.setAttribute('placeholder', '');

                            numberField.render('#card-number').then(() => this.FieldLoaded('number'));
                            expiryField.render('#expiry-date').then(() => this.FieldLoaded('expiry'));
                            cvvField.render('#cvv').then(() => this.FieldLoaded('cvv'));
                            nameField.render('#cardholder-name').then(() => this.FieldLoaded('name'));
                        } else {
                            throw Error('PayPal card fields could not be loaded');
                        }
                    } else {
                        throw Error('PayPal card fields could not be loaded');
                    }
                } else {
                    this.FieldLoaded('number');
                    this.FieldLoaded('expiry');
                    this.FieldLoaded('cvv');
                    this.FieldLoaded('name');   
                }
            });
        });
    }

    PaymentErrorDismissed() {
        this.form.Enable();
        this.Scope.PaymentProcessorReady();
    }

    private FieldLoaded(field: 'buttons' | 'number' | 'expiry' | 'cvv' | 'name') {
        switch (field) {
            case 'buttons': this._loadingPayPalButtons = false; break;
            case 'number': this._loadingCardNumberField = false; break;
            case 'expiry': this._loadingExpiryField = false; break;
            case 'cvv': this._loadingCVVField = false; break;
            default: this._loadingNameField = false; break;
        }

        if (!this._loadingPayPalButtons && !this._loadingCardNumberField && !this._loadingExpiryField && !this._loadingCVVField && !this._loadingNameField) {
            setTimeout(() => this._NgZone.run(() => {
                this._loading = false;
                this.Scope.PaymentProcessorReady();
            }), 1000);
        }
    }

    private UpdateCardFieldValidity(field: any, formControl: FormControl<string>, blur: boolean) {
        if (blur) {
            formControl.markAsTouched();
        }
        if (!field.isEmpty) {
            if (!field.isValid) {
                formControl.SetApiError();
            } else {
                formControl.setErrors(null);
            }
        } else {
            formControl.setErrors({required: true});
        }
    }

    ProcessPaymentSource() {
        this.form.CheckValidity().subscribe(result => {
            if (result) {
                this._cardFields.submit().catch((_: any) => {
                    this.form.Enable();
                    this.UpdateCardFields()
                });
            } else {
                this.UpdateCardFields();
            }
        });
    }

    protected ToggleDebugNewTab() {
        this._debugNewTab = !this._debugNewTab;
    }

    private UpdateCardFields() {
        this._cardFields.getState().then((data: any) => {
            this._NgZone.run(() => {
                this.UpdateCardFieldValidity(data.fields.cardNumberField, this.form.controls.CardNumber, true);
                this.UpdateCardFieldValidity(data.fields.cardExpiryField, this.form.controls.ExpiryDate, true);
                this.UpdateCardFieldValidity(data.fields.cardCvvField, this.form.controls.CVV, true);
                this.UpdateCardFieldValidity(data.fields.cardNameField, this.form.controls.CardHolderName, true);
                
                this.Scope.PaymentProcessorReady();
            });
        });
    }

    private UpdateAmountWithSurcharge(amountWithSurcharge: SurchargeResultItem): void {
        this._totalAmountToPay = amountWithSurcharge.Total;
        this._onlineConvenienceFee = amountWithSurcharge.Surcharge;
    }

    private ProcessNewPaymentSource(vaultSetupToken?: string) {
        const inputItem = new PayPalCommercePlatformPaymentSourceInputItem({
            ParentID: this.Scope.paymentDetails.ParentID,
            FamilyID: this.Scope.paymentDetails.FamilyID,
            PaymentProcessorID: this.Scope.paymentProcessor!.ID,
            VaultSetupToken: vaultSetupToken,
            OrderID: this._orderID
        });

        switch (this.Scope.mode) {
            case PaymentSourceMode.Add:
                inputItem.Intent = PaymentSourceIntent.Vault;
                inputItem.OrderID = this.isDebug ? this.form.controls.PostalCode.value : null;
                break;
            case PaymentSourceMode.Payment:
            case PaymentSourceMode.AnonymousPayment:
                inputItem.Intent = (this.form.controls.SavePaymentSource.value) ? PaymentSourceIntent.PayAndVault : PaymentSourceIntent.Pay;
                inputItem.Surcharge = this._onlineConvenienceFee ?? 0;
                inputItem.Total = this._totalAmountToPay;
                inputItem.VaultSetupToken = this.isDebug ? this.form.controls.PostalCode.value : null;
                break;
        }

        if (inputItem.Intent !== PaymentSourceIntent.Pay) {
            inputItem.AutoPay = this.form.controls.AutoPay.value;
        }

        this.Scope.ProcessPaymentSourceComplete(inputItem);
    }

    private CreateOrder(paymentSourceType: PaymentSourceType): Observable<PayPalCommercePlatformOrderResultItem> {
        const formValues = this.form.value;

        const body = new PayPalCommercePlatformCreateOrderInputItem({
            Total: this._totalAmountToPay!,
            Surcharge: this._onlineConvenienceFee ?? 0,
            PaymentSourceType: paymentSourceType,
            ParentID: this.Scope.paymentDetails.ParentID,
            FamilyID: this.Scope.paymentDetails.FamilyID,
            Vault: this.Scope.paymentProcessor!.StoredPaymentMethodsSupported ? formValues.SavePaymentSource : false,
            AutoPay: this.Scope.paymentProcessor!.StoredPaymentMethodsSupported ? formValues.AutoPay : false
        });

        if (paymentSourceType === PaymentSourceType.CreditCard) {
            body.PostalCode = formValues.PostalCode!;
            body.CountryCode = formValues.Country!.Code;
            body.ProvStateCode = formValues.State!.Code;
        } else {
            body.ReturnURL = window.location.href;
            body.PostalCode = this.Scope.paymentProcessor!.StoredPaymentMethodsSupported ? // Need to pass in postal code to interpret debugging codes
                (this.isDebug ? formValues.PostalCode! : null): //card fields
                ((this.isDebug && this._debugNewTab) ? "NEWTAB" : null);  //express
        }

        const observable = ApiRoutes.PaymentProcessor.PayPal.CommercePlatform.CreateOrder.Call({
            Parameter1: this.Scope.schoolInfo.ID,
            Parameter2: this.Scope.paymentProcessor!.ID!,
            Body: body
        });

        observable.subscribe({
            error: (error: typeof ApiRoutes.PaymentProcessor.PayPal.CommercePlatform.CreateOrder.Error) => {
                if (error.Response?.ErrorCode === ErrorCode.OnlinePaymentError) {
                    this.CreateDialog(PaymentErrorDialogComponent, { IsProcessorError: true, ErrorMessage: error.Response.ErrorMessage! });
                } else {
                    throw error;
                }
            }
        });

        return observable;
    }

    private GetCountries() {
        this.form.controls.Country.isLoading = true;

        ApiRoutes.Country.Get.Call().subscribe(countries => {
            this._countries = countries.map(country => new NameCodeWithID({...country, ID: country.Code!}));

            let defaultCountry: NameCodeWithID | undefined;

            defaultCountry = this._countries.find(country => country.Default);

            if (!defaultCountry) {
                defaultCountry = this._countries[0];
            }

            this.form.controls.Country.setValue(defaultCountry);
            this.form.controls.Country.isLoading = false;

            this.GetStates(defaultCountry);
        });
    }

    GetStates(country: NameCodePair) {
        this.form.controls.State.isLoading = true;

        ApiRoutes.Country.GetProvStates.Call({Parameter: country.Code!}).subscribe(states => {
            if (states && states.length > 0) {
                this._states = states.map(state => new NameCodeWithID({...state, ID: state.Code!}));

                const defaultState = this._states.find(state => state.Default);
                if (defaultState) {
                    this.form.controls.State.setValue(defaultState);
                } else {
                    this.form.controls.State.setValue(null);
                }
            } else {
                this._states = [this._defaultState];
                this.form.controls.State.setValue(this._defaultState);
            }

            this.form.controls.State.isLoading = false;
        });
    }

    PayInNewTab() {
        if (this.isDebug) {
            this.form.Disable();

            this.CreateOrder(PaymentSourceType.PayPal).subscribe(result => {
                window.open(result.PayPalOrderID!, '_blank');
            });
        }
    }

    get loading(): boolean {
        return this._loading;
    }

    get totalAmountToPay(): number | null {
        return this._totalAmountToPay;
    }

    get countries(): NameCodeWithID[] {
        return this._countries;
    }

    get states(): NameCodeWithID[] {
        return this._states;
    }

    get paymentSourceType(): PaymentSourceType {
        return this._paymentSourceType;
    }

    get cvvTooltip(): string {
        return this._cvvTooltip;
    }
    
    get cardSurcharge() {
        return this.Scope.schoolInfo.PaymentSurchargeCreditCard;
    }

    get PaymentSourceType(): typeof PaymentSourceType {
        return PaymentSourceType;
    }

    get SurchargeType(): typeof SurchargeType {
        return SurchargeType;
    }

    get PaymentSourceMode(): typeof PaymentSourceMode {
        return PaymentSourceMode;
    }

    get PaymentContext(): typeof PaymentContext {
        return PaymentContext;
    }

    get FormContainerKind(): typeof FormContainerKind {
        return FormContainerKind;
    }

    get isDebug(): boolean {
        return (this._Environment.Application ?? (this._Environment as any).APPLICATION).ProductName === "Test443" || (this._Environment.Application ?? (this._Environment as any).APPLICATION).ProductName === "Local";
    }

    get showNewWindowOverlay(): boolean {
        return this.isDebug && (this.form.controls.PostalCode.value.includes('NEWTAB') || this._debugNewTab);
    }

    get debugNewTab(): boolean {
        return this._debugNewTab;
    }
}
